import type { TBackofficeEnv } from "@/api/api-utils";
import type { BackofficeClient } from "@/api/backoffice-v2/backoffice-client";
import type {
  ICreateSiteRequestPayload,
  IReplaceSiteAddressV2Payload,
  IUpdateSiteAddressV2Payload,
  IUpdateSiteRequestPayload,
  TReplaceSiteAddressResponse,
  TSiteAddressResponse,
} from "@/api/backoffice-v2/backoffice-payloads";
import type { SiteQuerySource } from "@/api/backoffice-v2/site/site-request-context";
import type { GatewayShipmentRequestPayload } from "@/domains/gateway-shipment/gateway-shipment.types";
import type { V2V5InstallationTestId } from "@/domains/install-tests/installation-tests.ts";
import type { SiteModel } from "@/domains/site/site.types";
import type {
  EndCallRequestPayload,
  VoltacallTransferSignatureRequestPayload,
} from "@/domains/voltacall/voltacall.types";
import type { ModulatorHwId, SiteId } from "@/types/common/ids";

import * as Sentry from "@sentry/vue";

// TODO : restrict generated sdk usage to **only** this file
import {
  createSiteV2,
  deleteApiSiteBySiteIdVoltacallSubscriberTransferSignature,
  getApiGatewayShipmentModelTypes,
  getApiGatewayShipmentReasons,
  getApiReferenceSupportSubscriberNeeds,
  getApiSiteBySiteIdGatewayShipmentHistory,
  getApiSiteBySiteIdVoltacallSubscriberDetails,
  getApiSiteBySiteIdVoltacallSubscriberTransferSignature,
  getInstallTestResultsByTestId,
  getInstallTestsByModulatorHwId,
  getSiteV2,
  postApiPhoningSiteBySiteIdEndCall,
  postApiSiteBySiteIdGatewayShipmentRequest,
  postApiSiteBySiteIdVoltacallSubscriberTransferSignature,
  replaceSiteAddressV2,
  retrieveVxRexTests,
  updateSiteAddressV2,
  updateSiteV2,
} from "@/.generated/hey/backoffice-backend/sdk.gen";
import {
  zCreateSiteV2Response,
  zGetApiGatewayShipmentModelTypesResponse,
  zGetApiGatewayShipmentReasonsResponse,
  zGetApiReferenceSupportSubscriberNeedsResponse,
  zGetApiSiteBySiteIdGatewayShipmentHistoryResponse,
  zGetApiSiteBySiteIdVoltacallSubscriberDetailsResponse,
  zGetApiSiteBySiteIdVoltacallSubscriberTransferSignatureResponse,
  zGetInstallTestResultsByTestIdResponse,
  zGetInstallTestsByModulatorHwIdResponse,
  zGetSiteV2Response,
  zPostApiSiteBySiteIdVoltacallSubscriberTransferSignatureResponse,
  zReplaceSiteAddressV2Response,
  zRetrieveVxRexTestsResponse,
  zUpdateSiteAddressV2Response,
  zUpdateSiteV2Response,
} from "@/.generated/hey/backoffice-backend/zod.gen";
import { handleBackofficeResult } from "@/api/api-result.ts";
import { _makeSdkClient } from "@/api/backoffice-v2/backoffice-client-sdk.utils";
import {
  mapGatewayShipmentModelTypesResponse,
  mapGatewayShipmentReasonsResponse,
  mapGatewayShipmentRequestHistoryResponse,
} from "@/api/backoffice-v2/gateway-shipment/gateway-shipment.mapping";
import {
  mapV2V5InstallationTestResults,
  mapV2V5InstallationTests,
  mapVxInstallationTests,
} from "@/api/backoffice-v2/install-test/install-test.mapping.ts";
import { mapSupportSubscriberNeedsResponse } from "@/api/backoffice-v2/reference/support-subscriber-need.mapping";
import { mapReplaceSiteAddress, mapSiteAddress } from "@/api/backoffice-v2/site/site-address.mapping";
import { getSiteRequestHeaders, voltacallRequestContext } from "@/api/backoffice-v2/site/site-request-context";
import { mapSite } from "@/api/backoffice-v2/site/site.mapping";
import {
  mapVoltacallSubscriberDetails,
  mapVoltacallTransferSignatureResponse,
} from "@/api/backoffice-v2/voltacall/voltacall.mapping";
import { createBestEffortValidate } from "@/lib/validation-utils.ts";

/**
 * Validate a response at runtime, but does not block the flow in case of validation failure.
 * @param validator - Zod schema to validate against
 * @param errorMessage - Optional custom error message for Sentry logging
 */
const bestEffortValidate = createBestEffortValidate("Backend response does not match OpenAPI contract");
/**
 * Create an instance of a BackofficeClient backed by the SDK generated from
 * the OpenAPI specification.
 *
 * @param env - Environment variables required to configure the client.
 */
export function makeSdkBackofficeClient(env: TBackofficeEnv): BackofficeClient {
  const { client, headers } = _makeSdkClient(env);
  const requestOpts = {
    client,
    headers: {
      ...headers,
      "Content-Type": "application/json",
    },
    throwOnError: false,
    responseStyle: "fields",
  } as const;

  return {
    reference: {
      async getSubscriberNeeds() {
        return getApiReferenceSupportSubscriberNeeds(requestOpts)
          .then(handleBackofficeResult)
          .then(bestEffortValidate(zGetApiReferenceSupportSubscriberNeedsResponse))
          .then(mapSupportSubscriberNeedsResponse);
      },
    },
    site: {
      async getById(siteId: SiteId, querySource: SiteQuerySource): Promise<SiteModel> {
        return Sentry.startSpan(
          {
            name: "GET /api/site/v2/{siteId}",
            op: "http.client",
            attributes: {
              "http.request.method": "GET",
              "http.route": "/api/site/v2/{siteId}",
              "voltacall.site.id": siteId,
              "voltacall.query.source": querySource,
              "voltacall.tab.id": voltacallRequestContext.tabId,
              "voltacall.page_load.id": voltacallRequestContext.pageLoadId,
              "voltacall.document.visibility": globalThis.document.visibilityState,
              "voltacall.network.online": globalThis.navigator.onLine,
            },
          },
          () =>
            getSiteV2({
              ...requestOpts,
              path: { siteId },
              headers: {
                ...requestOpts.headers,
                ...getSiteRequestHeaders(querySource),
              },
            })
              .then(handleBackofficeResult)
              .then(bestEffortValidate(zGetSiteV2Response))
              .then(mapSite),
        );
      },
      async createSite(body: ICreateSiteRequestPayload): Promise<SiteModel> {
        return createSiteV2({ ...requestOpts, body })
          .then(handleBackofficeResult)
          .then(bestEffortValidate(zCreateSiteV2Response))
          .then(mapSite);
      },
      async updateSite(siteId: SiteId, body: IUpdateSiteRequestPayload): Promise<SiteModel> {
        return updateSiteV2({ ...requestOpts, path: { siteId }, body })
          .then(handleBackofficeResult)
          .then(bestEffortValidate(zUpdateSiteV2Response))
          .then(mapSite);
      },
      async replaceSiteAddress(
        siteId: SiteId,
        body: IReplaceSiteAddressV2Payload,
      ): Promise<TReplaceSiteAddressResponse> {
        return replaceSiteAddressV2({ ...requestOpts, path: { siteId }, body })
          .then(handleBackofficeResult)
          .then(bestEffortValidate(zReplaceSiteAddressV2Response))
          .then(mapReplaceSiteAddress);
      },
      async updateSiteAddress(siteId: SiteId, body: IUpdateSiteAddressV2Payload): Promise<TSiteAddressResponse> {
        return updateSiteAddressV2({
          ...requestOpts,
          path: { siteId },
          body,
        })
          .then(handleBackofficeResult)
          .then(bestEffortValidate(zUpdateSiteAddressV2Response))
          .then(mapSiteAddress);
      },
    },
    transferSignature: {
      async getSubscriberDetails(siteId: SiteId) {
        return getApiSiteBySiteIdVoltacallSubscriberDetails({
          ...requestOpts,
          path: { siteId },
        })
          .then(handleBackofficeResult)
          .then(bestEffortValidate(zGetApiSiteBySiteIdVoltacallSubscriberDetailsResponse))
          .then(mapVoltacallSubscriberDetails);
      },
      async getTransferSignature(siteId: SiteId) {
        const response = await getApiSiteBySiteIdVoltacallSubscriberTransferSignature({
          ...requestOpts,
          path: { siteId },
        });

        if (!response.response.ok && response.response.status === 404) {
          return null;
        }

        const data = bestEffortValidate(zGetApiSiteBySiteIdVoltacallSubscriberTransferSignatureResponse)(
          handleBackofficeResult(response),
        );
        return mapVoltacallTransferSignatureResponse(data);
      },
      async createTransferSignature(siteId: SiteId, payload: VoltacallTransferSignatureRequestPayload) {
        return postApiSiteBySiteIdVoltacallSubscriberTransferSignature({
          ...requestOpts,
          path: { siteId },
          body: payload,
        })
          .then(handleBackofficeResult)
          .then(bestEffortValidate(zPostApiSiteBySiteIdVoltacallSubscriberTransferSignatureResponse))
          .then(mapVoltacallTransferSignatureResponse);
      },
      async cancelTransferSignature(siteId: SiteId) {
        await deleteApiSiteBySiteIdVoltacallSubscriberTransferSignature({
          ...requestOpts,
          path: { siteId },
        }).then(handleBackofficeResult);
      },
    },
    phoning: {
      async endCall(siteId: SiteId, body: EndCallRequestPayload) {
        await postApiPhoningSiteBySiteIdEndCall({
          ...requestOpts,
          path: { siteId },
          headers: { ...requestOpts.headers, serviceId: env.VITE_VOLTACALL_SERVICE_ID },
          body,
        }).then(handleBackofficeResult);
      },
    },
    gatewayShipment: {
      async getReasons() {
        return getApiGatewayShipmentReasons(requestOpts)
          .then(handleBackofficeResult)
          .then(bestEffortValidate(zGetApiGatewayShipmentReasonsResponse))
          .then(mapGatewayShipmentReasonsResponse);
      },
      async getModelTypes() {
        return getApiGatewayShipmentModelTypes(requestOpts)
          .then(handleBackofficeResult)
          .then(bestEffortValidate(zGetApiGatewayShipmentModelTypesResponse))
          .then(mapGatewayShipmentModelTypesResponse);
      },
      async getHistory(siteId: SiteId) {
        return getApiSiteBySiteIdGatewayShipmentHistory({ ...requestOpts, path: { siteId } })
          .then(handleBackofficeResult)
          .then(bestEffortValidate(zGetApiSiteBySiteIdGatewayShipmentHistoryResponse))
          .then(mapGatewayShipmentRequestHistoryResponse);
      },
      async requestShipment(siteId: SiteId, payload: GatewayShipmentRequestPayload) {
        await postApiSiteBySiteIdGatewayShipmentRequest({
          ...requestOpts,
          path: { siteId },
          body: payload,
        }).then(handleBackofficeResult);
      },
    },
    installationTests: {
      async getVxBySite(siteId: SiteId) {
        return retrieveVxRexTests({ ...requestOpts, path: { siteId } })
          .then((response) => zRetrieveVxRexTestsResponse.parse(response.data))
          .then(mapVxInstallationTests);
      },
      async getV2V5ByModulatorHardwareId(modulatorHwId: ModulatorHwId) {
        return getInstallTestsByModulatorHwId({ ...requestOpts, path: { modulatorHwId } })
          .then((response) => zGetInstallTestsByModulatorHwIdResponse.parse(response.data))
          .then(mapV2V5InstallationTests);
      },
      async getV2V5ResultsById(installTestId: V2V5InstallationTestId) {
        return getInstallTestResultsByTestId({ ...requestOpts, path: { installTestId } })
          .then((response) => zGetInstallTestResultsByTestIdResponse.parse(response.data))
          .then(mapV2V5InstallationTestResults);
      },
    },
  };
}
